import { Component, OnInit, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { OnboardingFacade } from 'app/main/pages/customer-onboard-flow/store/onboarding/onboarding.facade';
import { Observable, Subject, takeUntil } from 'rxjs';
import { CustomerOnboardFlowService } from 'app/main/pages/customer-onboard-flow/customer-onboard-flow.service';
import { ImageService } from 'app/global/image.service';

import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { TranslocoModule } from '@jsverse/transloco';
import { NgSelectModule } from '@ng-select/ng-select';
import { CoreDirectivesModule } from '@core/directives/directives';
import { SharedModule } from 'app/main/shared/shared/shared.module';

@Component({
  selector: 'app-onboarding-user-profile',
  templateUrl: './onboarding-user-profile.component.html',
  styleUrls: ['./onboarding-user-profile.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslocoModule,
    NgSelectModule,
    CoreDirectivesModule,
    SharedModule
  ]
})
export class OnboardingUserProfileComponent implements OnInit, OnDestroy {
  @Input() userProfileForm!: FormGroup;
  @Input() isInvitedOnboarding: boolean;
  @Input() inviteFormDisabled!: boolean;

  @Output() continueClicked = new EventEmitter<void>();

  profileImagePreview$: Observable<string | null>;
  passwordTextType$: Observable<boolean>;
  submitted$: Observable<boolean>;
  public profileImagePreview: string | null = null;
  public passwordTextType: boolean = false;
  public selectedCountry: { name: string, dialCode: string } = { name: 'Sweden', dialCode: '46' };

  jobTitleList: { name: string; }[] = [];

  private _unsubscribeAll: Subject<void> = new Subject();

  constructor(
    private _onboardingFacade: OnboardingFacade,
    private _customerOnboardFlowService: CustomerOnboardFlowService,
    private _imageService$: ImageService,
  ) {
    this.profileImagePreview$ = this._onboardingFacade.userProfileImagePreview$;
    this.passwordTextType$ = this._onboardingFacade.userProfilePasswordTextType$;
    this.submitted$ = this._onboardingFacade.userProfileSubmitted$;
    this.jobTitleList = this._customerOnboardFlowService.fetchJobTitleList();
  }

  get passwordControl(): FormControl {
    return this.userProfileForm.get('password') as FormControl;
  }

  get termsControl(): FormControl {
    return this.userProfileForm.get('terms') as FormControl;
  }

  get companyNameControl(): FormControl {
    return this.userProfileForm.get('companyName') as FormControl;
  }

  get firstNameControl(): FormControl {
    return this.userProfileForm.get('firstName') as FormControl;
  }

  get lastNameControl(): FormControl {
    return this.userProfileForm.get('lastName') as FormControl;
  }

  get jobTitleControl(): FormControl {
    return this.userProfileForm.get('jobTitle') as FormControl;
  }

  ngOnInit(): void {
    this.profileImagePreview$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(preview => {
        this.profileImagePreview = preview;
      });

    this.passwordTextType$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(type => { this.passwordTextType = type; });
  }

  onProfileImageUpload(event: any): void {
    const file = event.target.files[0];
    if (!file) return;

    const imageControl = this.userProfileForm.get('profileImage');
    imageControl.setValue(file);
    imageControl.markAsTouched();

    if (imageControl.valid) {
      this._imageService$.loadImage(file)
        .then(dataURL => {
          this._onboardingFacade.updateProfileImage(dataURL);
          this._onboardingFacade.setProfileImageFile(file);
        })
        .catch(error => {
          console.warn('Error loading image:', error);
          this._onboardingFacade.updateProfileImage(null);
          this._onboardingFacade.setProfileImageFile(null);
        });
    } else {
      this._onboardingFacade.updateProfileImage(null);
      this._onboardingFacade.setProfileImageFile(null);
    }
  }

  onTogglePasswordTextType(): void {
    this._onboardingFacade.togglePasswordTextType();
  }

  onCountrySelected(country: { name: string, dialCode: string }): void {
    this.selectedCountry = country;
  }

  onContinueClick(): void {
    if (this.inviteFormDisabled) return;
    this._onboardingFacade.setUserProfileFormSubmitted(true);

    if (this.userProfileForm.invalid) {
      this.userProfileForm.markAllAsTouched();
      return;
    }

    const phoneControl = this.userProfileForm.get('phone') as FormControl;
    const formattedPhone = this._customerOnboardFlowService.fixPhoneNumber(phoneControl);

    if (!formattedPhone) {
      phoneControl.setErrors({ 'invalidPhone': true }); // Manually set error
      return;
    }

    // If valid, update the control's value and then the store
    phoneControl.setValue(formattedPhone, { emitEvent: false }); // Update control, but don't emit valueChanges
    this._onboardingFacade.updateUserProfileField('phoneNumber', formattedPhone); // Update store with formatted value

    // Update all other fields in the store at once
    this._onboardingFacade.updateUserProfileField('jobTitle', this.userProfileForm.get('jobTitle').value);

    this._onboardingFacade.updateUserProfileField('noMarketing', this.userProfileForm.get('noMarketing').value);

    if (this.isInvitedOnboarding) {
      this._onboardingFacade.updateUserProfileField('firstName', this.userProfileForm.get('firstName').value);
      this._onboardingFacade.updateUserProfileField('lastName', this.userProfileForm.get('lastName').value);
      this._onboardingFacade.updateUserProfileField('password', this.userProfileForm.get('password').value);
      this._onboardingFacade.updateUserProfileField('terms', this.userProfileForm.get('terms').value);
    } else {
      this._onboardingFacade.updateUserProfileField('companyName', this.userProfileForm.get('companyName').value);
    }

    this.continueClicked.emit();
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }
}